import React, { useState, useEffect, useContext } from "react"
import PropTypes from "prop-types"
import { compose } from "recompose"
import capitalize from "lodash.capitalize"
import { useToasts } from "react-toast-notifications"
import { useTranslation } from "react-i18next"

import {
  Box,
  Input,
  Textarea,
  Button,
  VStack,
  Heading,
  Image,
  Text,
  ChakraProvider,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react"

import Nav from "../../components/atoms/Nav"
import { withFirebase } from "../../utils/Firebase"
import { withAuthorization, AuthUserContext } from "../../context/Session"
import * as ROLES from "../../utils/Constants/Roles"
import { postCreatePushNotification } from "../../utils/App"

const PushNotificationForm = ({ firebase }) => {
  const authUser = useContext(AuthUserContext)
  const { addToast } = useToasts()
  const { t } = useTranslation()

  const [orgLocationList, setOrgLocationList] = useState([])
  const [locationsLoading, setLocationsLoading] = useState(false)
  const [locationsError, setLocationsError] = useState(null)

  const [sedesList, setSedesList] = useState([])
  const [departmentsOptions, setDepartmentsOptions] = useState([])
  const [selectedDepartments, setSelectedDepartments] = useState([])
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [file, setFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [fileError, setFileError] = useState("")
  const [includeLink, setIncludeLink] = useState(false)
  const [link, setLink] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const fetchLocations = async () => {
    if (!authUser?.organizationId) return
    setLocationsLoading(true)
    setLocationsError(null)
    try {
      const orgLocations = await firebase.getOrgLocationList(
        authUser.organizationId
      )
      setOrgLocationList(orgLocations)
    } catch (err) {
      console.error("Error loading locations", err)
      setLocationsError("No se pudieron cargar las sedes.")
    } finally {
      setLocationsLoading(false)
    }
  }

  useEffect(() => {
    fetchLocations()
  }, [authUser, firebase])

  useEffect(() => {
    const fetchDepartments = async () => {
      if (sedesList.length > 0 && authUser?.organizationId) {
        const departmentsData = await firebase.getOrgDepartmentsByLocations(
          authUser.organizationId,
          sedesList
        )
        const options = departmentsData.map((dept) => ({
          id: dept.id,
          displayName: dept.displayName,
          locationId: dept.locationId,
        }))
        setDepartmentsOptions(options)

        if (selectedDepartments.includes("ALL_DEPARTMENTS")) {
          setSelectedDepartments([
            "ALL_DEPARTMENTS",
            ...options.map((o) => o.id),
          ])
        }
      } else {
        setDepartmentsOptions([])
        setSelectedDepartments([])
      }
    }

    fetchDepartments()
  }, [sedesList, authUser, firebase])

  const resetForm = () => {
    setTitle("")
    setBody("")
    setFile(null)
    setFilePreview(null)
    setFileError("")
    setIncludeLink(false)
    setLink("")
    setSedesList([])
    setSelectedDepartments([])
    setDepartmentsOptions([])
    setIsLoading(false)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    if (fileError) {
      addToast(t("unexpected_error"), {
        appearance: "error",
        placement: "bottom-center",
      })
      return
    }

    if (title.trim() === "" || body.trim() === "") {
      addToast("Título y cuerpo son requeridos.", {
        appearance: "error",
        placement: "bottom-center",
      })
      return
    }

    try {
      let agents = []
      const locationIds = sedesList.includes("ALL_SEDES")
        ? orgLocationList.map((s) => s.id)
        : sedesList

      if (
        selectedDepartments.length === 0 ||
        selectedDepartments.includes("ALL_DEPARTMENTS")
      ) {
        agents = await firebase.getAgentsByLocation(
          authUser.organizationId,
          locationIds
        )
      } else {
        const departmentsData = await firebase.getOrgDepartmentsByLocations(
          authUser.organizationId,
          sedesList
        )
        const relevantDepartments = departmentsData.filter((dept) =>
          selectedDepartments.includes(dept.id)
        )
        const departmentIds = relevantDepartments.map((dept) => dept.id)

        agents = await firebase.getAgentsByDepartments(
          authUser.organizationId,
          departmentIds
        )
      }

      const recipients = agents.map((agent) => agent.email).filter(Boolean)

      let imageUrl = null
      if (file) {
        const uploadDir = `organizations/${authUser.organizationId}/notifications/${file.name}`
        const snapshot = await firebase.uploadToStorage(uploadDir, file)
        imageUrl = await snapshot.ref.getDownloadURL()
      }

      const senderUUID = crypto.randomUUID()
      const payload = {
        title,
        body,
        ...(imageUrl && { imageUrl }),
        recipients,
        data: {
          uuid: senderUUID,
          category: "general",
          ...(includeLink && link && { link }),
        },
      }

      const token = await firebase.getIdToken()

      const res = await fetch(postCreatePushNotification, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })

      if (!res.ok) {
        throw new Error("Push notification request failed")
      }

      const notificationRecord = {
        senderUUID,
        title,
        body,
        imageUrl,
        link: includeLink ? link : null,
        recipientCount: recipients.length,
        recipients,
        sentAt: new Date(),
        sentBy: {
          id: authUser.uid,
          email: authUser.email,
        },
        sedes: sedesList,
        departments: selectedDepartments,
      }

      await firebase.saveNotification({
        orgId: authUser.organizationId,
        notification: notificationRecord,
      })

      addToast("Notificación registrada exitosamente.", {
        appearance: "success",
        placement: "bottom-center",
      })

      resetForm()
    } catch (error) {
      console.error("handleSubmit error", error)
      addToast("Error preparando la notificación", {
        appearance: "error",
        placement: "bottom-center",
      })
    }
    setIsLoading(false)
  }

  const handleChange = async (event) => {
    const selectedFile = event.target.files[0]

    if (selectedFile) {
      const validTypes = ["image/jpeg", "image/png"]
      const fileType = selectedFile.type

      if (!validTypes.includes(fileType)) {
        setFile(null)
        setFilePreview(null)
        setFileError("Solo se permiten imágenes en formato JPG o PNG")
        return
      }

      if (selectedFile.size > 300 * 1024) {
        setFile(null)
        setFilePreview(null)
        setFileError("El archivo debe ser menor de 300KB")
        return
      }

      setFile(selectedFile)
      setFilePreview(URL.createObjectURL(selectedFile))
      setFileError("")
    }
  }

  return (
    <ChakraProvider>
      <Nav titleKey="messaging_tite" descriptionKey="messaging_description" />

      <Box p={6} maxW="600px" mx="auto">
        <Heading mb={6}>Enviar Push Notification</Heading>
        <VStack spacing={4} align="stretch">
          {/* Location Selector */}
          <Box>
            <Text fontWeight="semibold" mb={1}>
              Seleccione sedes
            </Text>

            {locationsLoading && (
              <Text color="gray.500">Cargando sedes...</Text>
            )}

            {locationsError && (
              <VStack align="start" spacing={2}>
                <Text color="red.500">{locationsError}</Text>
                <Button size="sm" onClick={fetchLocations}>
                  Reintentar
                </Button>
              </VStack>
            )}

            {!locationsLoading &&
              !locationsError &&
              orgLocationList.length > 0 && (
                <CheckboxGroup
                  value={sedesList}
                  onChange={(values) => {
                    const wasAllChecked = sedesList.includes("ALL_SEDES")
                    const isAllNowChecked = values.includes("ALL_SEDES")

                    if (!wasAllChecked && isAllNowChecked) {
                      setSedesList([
                        "ALL_SEDES",
                        ...orgLocationList.map((s) => s.id),
                      ])
                    } else if (wasAllChecked && !isAllNowChecked) {
                      setSedesList([])
                    } else {
                      setSedesList(values.filter((v) => v !== "ALL_SEDES"))
                    }
                  }}
                >
                  <Stack spacing={2}>
                    <Checkbox value="ALL_SEDES">Todas las sedes</Checkbox>
                    {orgLocationList.map((sede) => (
                      <Checkbox key={sede.id} value={sede.id}>
                        {capitalize(sede.displayName)}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              )}
          </Box>

          {/* Department Selector */}
          {sedesList.length > 0 && (
            <Box>
              <Text fontWeight="semibold" mb={1}>
                Seleccione departamentos
              </Text>
              <CheckboxGroup
                value={selectedDepartments}
                onChange={(values) => {
                  const wasAllChecked =
                    selectedDepartments.includes("ALL_DEPARTMENTS")
                  const isAllNowChecked = values.includes("ALL_DEPARTMENTS")

                  if (!wasAllChecked && isAllNowChecked) {
                    setSelectedDepartments([
                      "ALL_DEPARTMENTS",
                      ...departmentsOptions.map((d) => d.id),
                    ])
                  } else if (wasAllChecked && !isAllNowChecked) {
                    setSelectedDepartments([])
                  } else {
                    setSelectedDepartments(
                      values.filter((v) => v !== "ALL_DEPARTMENTS")
                    )
                  }
                }}
              >
                <Stack spacing={2}>
                  <Checkbox value="ALL_DEPARTMENTS">
                    Todos los departamentos
                  </Checkbox>
                  {departmentsOptions.map((dept) => {
                    const sedeName =
                      orgLocationList.find((s) => s.id === dept.locationId)
                        ?.displayName || ""
                    return (
                      <Checkbox key={dept.id} value={dept.id}>
                        <Text as="span" color="gray.600">
                          {dept.displayName}
                        </Text>{" "}
                        <Text as="span" fontWeight="bold">
                          ({capitalize(sedeName)})
                        </Text>
                      </Checkbox>
                    )
                  })}
                </Stack>
              </CheckboxGroup>
            </Box>
          )}

          <Input
            placeholder="Título (corto)"
            value={title}
            maxLength={60}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Textarea
            placeholder="Cuerpo (max 200 carácteres)"
            value={body}
            maxLength={200}
            onChange={(e) => setBody(e.target.value)}
          />

          <Box>
            <Checkbox
              isChecked={includeLink}
              onChange={(e) => setIncludeLink(e.target.checked)}
            >
              Incluir un enlace
            </Checkbox>
            {includeLink && (
              <Input
                mt={2}
                placeholder="https://example.com"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
            )}
          </Box>

          <Box>
            <Input
              type="file"
              id="file-upload"
              display="none"
              accept="image/*"
              onChange={handleChange}
            />
            <Button as="label" htmlFor="file-upload" colorScheme="blue">
              Seleccione un archivo
            </Button>
            {file && !fileError && (
              <Text mt={2}>Archivo seleccionado: {file.name}</Text>
            )}
            {fileError && (
              <Text mt={1} color="red.500">
                {fileError}
              </Text>
            )}
          </Box>

          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={
              !!fileError ||
              sedesList.length === 0 ||
              title.trim() === "" ||
              body.trim() === ""
            }
            isLoading={isLoading}
          >
            Enviar notificación
          </Button>

          <Box
            mt={6}
            p={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Heading size="sm" mb={2}>
              Prevista
            </Heading>
            <Text textTransform="capitalize">
              <strong>Sedes:</strong>{" "}
              {sedesList.length > 0
                ? sedesList
                    .filter((id) => id !== "ALL_SEDES")
                    .map(
                      (id) =>
                        orgLocationList.find((sede) => sede.id === id)
                          ?.displayName
                    )
                    .filter(Boolean)
                    .join(", ")
                : "Ninguna seleccionada"}
            </Text>
            <Text textTransform="capitalize">
              <strong>Departamentos:</strong>{" "}
              {selectedDepartments.length > 0
                ? selectedDepartments
                    .filter((id) => id !== "ALL_DEPARTMENTS")
                    .map(
                      (id) =>
                        departmentsOptions.find((dept) => dept.id === id)
                          ?.displayName
                    )
                    .filter(Boolean)
                    .join(", ")
                : "Ninguno seleccionado"}
            </Text>
            <Text mt={2} fontWeight="bold">
              {title}
            </Text>
            <Text>{body}</Text>
            {includeLink && link && (
              <Text mt={2}>
                <strong>Enlace:</strong>{" "}
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3182ce" }}
                >
                  {link}
                </a>
              </Text>
            )}
            {filePreview && !fileError && (
              <Image
                src={filePreview}
                alt="Vista previa"
                mt={3}
                maxH="150px"
                objectFit="cover"
                borderRadius="md"
              />
            )}
          </Box>
        </VStack>
      </Box>
    </ChakraProvider>
  )
}

PushNotificationForm.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) =>
  ((!!authUser && authUser.userType === "admin") ||
    (!!authUser && authUser.userType === "business_partner")) &&
  authUser.hasMessagingAccess &&
  (authUser.roles[ROLES.BUSINESS_ADMIN] || authUser.roles[ROLES.COMMUNICATOR])

const ComposedPushNotificationForm = compose(
  withFirebase,
  withAuthorization(condition)
)(PushNotificationForm)

export default ComposedPushNotificationForm
